/* import __COLOCATED_TEMPLATE__ from './ask-a-teammate.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import type EditorConfig from 'embercom/objects/visual-builder/configuration/editor';
import type Fin from 'embercom/models/operator/visual-builder/step/fin';
import { tracked } from '@glimmer/tracking';

type Option = 'on' | 'off';

const MIN_THRESHOLD = 10;
const DEFAULT_THRESHOLD = 50;
const HOUR = 3600;
const DAY = 86400;
const MIN_TIMEOUT_SECONDS = HOUR;
const MAX_TIMEOUT_SECONDS = 30 * DAY;
const DEFAULT_TIMEOUT_SECONDS = 24 * HOUR;

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: $TSFixMe;
    step: $TSFixMe;
    isReadOnly: boolean;
    editorState: EditorState;
    editorConfiguration?: EditorConfig;
  };
}

export default class WorkflowAskATeammate extends Component<Signature> {
  @service declare appService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare store: $TSFixMe;

  @tracked selectedOption: Option = this.args.step?.hitlEnabled ? 'on' : 'off';
  @tracked selectedThreshold: number = this.args.step?.hitlConfidenceThreshold ?? DEFAULT_THRESHOLD;
  @tracked selectedAssigneeId: string | null =
    this.args.step?.hitlAssigneeId != null ? String(this.args.step.hitlAssigneeId) : null;
  @tracked selectedTimeoutSeconds: number =
    this.args.step?.hitlTimeoutSeconds ?? DEFAULT_TIMEOUT_SECONDS;

  private _localPredicateGroup: any = null;

  get step(): Fin {
    return this.args.step;
  }

  get canUseFinEmailHitl(): boolean {
    return Boolean(this.appService?.app?.canUseFeature?.('fin-email-hitl'));
  }

  get predicateGroup() {
    if (this.step.hitlConditions) {
      return this.step.hitlConditions;
    }
    if (!this._localPredicateGroup) {
      this._localPredicateGroup = this.store.createFragment('predicates/predicate-group');
    }
    return this._localPredicateGroup;
  }

  get attributeGroupList() {
    return this.args.editorConfiguration?.runtimeMatchingTargetingAttributes ?? [];
  }

  get statusLabel(): string {
    if (this.selectedOption !== 'on') {
      return this.intl.t('operator.fin.setup.setup-and-go-live.ask-a-teammate.disabled-status');
    }
    return this.intl.t('operator.fin.setup.setup-and-go-live.ask-a-teammate.enabled-status', {
      threshold: this.selectedThreshold,
    });
  }

  @action
  setSelectedOption(option: Option) {
    if (option === this.selectedOption) {
      return;
    }
    this.selectedOption = option;
    this.step.hitlEnabled = option === 'on';
    if (option === 'on') {
      this.step.hitlConfidenceThreshold = this.selectedThreshold;
      this.step.hitlAssigneeId =
        this.selectedAssigneeId != null ? Number(this.selectedAssigneeId) : null;
      this.step.hitlTimeoutSeconds = this.selectedTimeoutSeconds;
    }
  }

  @action
  setSelectedThreshold(threshold: number) {
    let clamped = Math.min(100, Math.max(MIN_THRESHOLD, threshold));
    this.selectedThreshold = clamped;
    this.step.hitlConfidenceThreshold = clamped;
  }

  @action
  setAssigneeId(id: string) {
    let assigneeId = id === '0' ? null : id;
    this.selectedAssigneeId = assigneeId;
    this.step.hitlAssigneeId = assigneeId != null ? Number(assigneeId) : null;
  }

  @action
  onPredicateGroupChange(predicates: any) {
    let copied = [...predicates];
    if (!this.step.hitlConditions) {
      this.step.set(
        'hitlConditions',
        this.store.createFragment('predicates/predicate-group', { predicates: copied }),
      );
    } else {
      this.step.hitlConditions.set('predicates', copied);
    }
  }

  @action
  setTimeoutSeconds(seconds: number) {
    let clamped = Math.min(MAX_TIMEOUT_SECONDS, Math.max(MIN_TIMEOUT_SECONDS, seconds));
    this.selectedTimeoutSeconds = clamped;
    this.step.hitlTimeoutSeconds = clamped;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Fin::Sections::AskATeammate': typeof WorkflowAskATeammate;
    'workflows/graph-editor/node-items/steps/fin/sections/ask-a-teammate': typeof WorkflowAskATeammate;
  }
}
